import React from "react";
import { twMerge } from "tailwind-merge";

interface StyledTextProps {
  children: React.ReactNode;
  className?: string;
  as?: React.ElementType;
}

// Large page title (26px/32px SemiBold - Source Serif Pro)
const LargePageTitleText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "h1",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-source-serif font-semibold text-[26px] leading-[32px] text-on-surface",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Section header (20px/24px SemiBold - Source Serif Pro)
const SectionHeaderText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "h2",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-source-serif font-semibold text-[20px] leading-[24px] text-on-surface",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Small page title (16px/24px Bold - Source Sans Pro)
const SmallPageTitleText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "h3",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-source-sans font-bold text-[16px] leading-[24px] text-on-surface",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Heading (16px/24px Bold - Source Sans Pro)
const HeadingText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "h4",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-source-sans font-bold text-[16px] leading-[24px] text-on-surface",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Stat (16px/24px Bold - Source Sans Pro)
const StatText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "span",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-source-sans font-bold text-[16px] leading-[24px] text-on-surface",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Button (16px/24px SemiBold - Source Sans Pro)
const ButtonText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "span",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-source-sans font-semibold text-[16px] leading-[24px] text-on-surface",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Body (16px/24px Regular - Source Sans Pro)
const BodyText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "p",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-source-sans font-normal text-[16px] leading-[24px] text-on-surface",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Text Link (16px/24px Regular with underline - Source Sans Pro)
const TextLinkText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "a",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-source-sans font-normal text-[16px] leading-[24px] text-on-surface underline",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Subheading (14px/20px Regular - Source Sans Pro)
const SubheadingText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "span",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-source-sans font-normal text-[14px] leading-[20px] text-on-surface",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Label (14px/20px Regular - Source Sans Pro)
const LabelText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "label",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-source-sans font-normal text-[14px] leading-[20px] text-on-surface",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Small label (12px/16px SemiBold - Source Sans Pro)
const SmallLabelText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "span",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-source-sans font-semibold text-[12px] leading-[16px] text-on-surface",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Insight (16px/24px SemiBold - Source Sans Pro)
const InsightText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "span",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-source-sans font-semibold text-[16px] leading-[24px] text-on-surface",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Export all components matching Figma design system
export {
  LargePageTitleText,
  SectionHeaderText,
  SmallPageTitleText,
  HeadingText,
  StatText,
  ButtonText,
  BodyText,
  TextLinkText,
  SubheadingText,
  LabelText,
  SmallLabelText,
  InsightText,
};
